import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { spreadTypes } from '../data/spreadTypes';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import { horoscopeService, PromoStatus } from '../services/horoscopeApi';
import TokenCostBadge, { canAfford } from '../components/TokenCostBadge';

type Category = 'all' | 'daily' | 'general' | 'love' | 'decision';

const CATEGORIES: { id: Category; label: string; emoji: string }[] = [
  { id: 'all',      label: 'Todas',   emoji: '✨' },
  { id: 'daily',    label: 'Diário',  emoji: '☀️' },
  { id: 'general',  label: 'Geral',   emoji: '🔮' },
  { id: 'love',     label: 'Amor',    emoji: '💕' },
  { id: 'decision', label: 'Decisão', emoji: '⚖️' },
];

const PALETTE: Record<string, { accent: string; accentSoft: string; glow: string; bar: string; btn: string; iconBg: string }> = {
  daily:    { accent: '#f59e0b', accentSoft: 'rgba(251,191,36,0.18)', glow: 'rgba(251,191,36,0.25)', bar: 'linear-gradient(to right,#d97706,#fbbf24)', btn: 'linear-gradient(135deg,#d97706,#f59e0b)', iconBg: 'rgba(251,191,36,0.15)' },
  general:  { accent: '#8b5cf6', accentSoft: 'rgba(139,92,246,0.18)', glow: 'rgba(139,92,246,0.25)', bar: 'linear-gradient(to right,#7c3aed,#a78bfa)', btn: 'linear-gradient(135deg,#7c3aed,#8b5cf6)', iconBg: 'rgba(139,92,246,0.15)' },
  love:     { accent: '#f43f5e', accentSoft: 'rgba(244,63,94,0.18)',  glow: 'rgba(244,63,94,0.25)',  bar: 'linear-gradient(to right,#be185d,#fb7185)', btn: 'linear-gradient(135deg,#be185d,#f43f5e)', iconBg: 'rgba(244,63,94,0.15)'  },
  decision: { accent: '#22d3ee', accentSoft: 'rgba(34,211,238,0.18)', glow: 'rgba(34,211,238,0.25)', bar: 'linear-gradient(to right,#0891b2,#38bdf8)', btn: 'linear-gradient(135deg,#0891b2,#22d3ee)', iconBg: 'rgba(34,211,238,0.15)' },
};

const EMOJI: Record<string, string> = { daily: '☀️', general: '🔮', love: '💕', decision: '⚖️' };

const TarotPage: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialCategory = (searchParams.get('cat') as Category) || 'all';
  const [activeCategory, setActiveCategory] = useState<Category>(
    CATEGORIES.some(c => c.id === initialCategory) ? initialCategory : 'all'
  );
  const [hovered, setHovered] = useState<string | null>(null);
  const [promo, setPromo] = useState<PromoStatus | null>(null);
  const cardBackUrl = (theme.images.cardBackImageUrl?.startsWith('http')) ? theme.images.cardBackImageUrl : undefined;

  useEffect(() => {
    horoscopeService.getPromo().then(setPromo).catch(() => {});
  }, []);

  const filtered = activeCategory === 'all' ? spreadTypes : spreadTypes.filter(s => s.category === activeCategory);
  const p = (cat: string) => PALETTE[cat] || PALETTE.general;

  return (
    <div className="min-h-screen bg-gradient-mystical theme-bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-5 border-b border-white/10">
        <Link to="/dashboard" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
          <ArrowLeft size={20} />
          <span className="text-sm">Voltar</span>
        </Link>
        <h1 className="text-xl font-mystical font-bold text-white">Tarot Rider-Waite</h1>
        <div className="w-20" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Intro */}
        <div className="text-center mb-6 sm:mb-10">
          <div className="w-14 h-20 sm:w-16 sm:h-24 mx-auto mb-3 rounded-xl overflow-hidden border-2 border-purple-500/40 shadow-lg shadow-purple-500/20">
            {cardBackUrl ? (
              <img src={cardBackUrl} alt="Tarot" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-purple-900 via-indigo-900 to-purple-800 flex items-center justify-center">
                <span className="text-purple-300 text-xl">✦</span>
              </div>
            )}
          </div>
          <h2 className="text-xl sm:text-3xl font-mystical font-bold text-white mb-2">Escolha sua Tiragem</h2>
          <p className="text-white/50 text-sm">Selecione o método de leitura que melhor responde à sua pergunta</p>
        </div>

        {/* Filtros — a cor de cada chip é a mesma cor da categoria nos cards abaixo,
            para a legenda ficar óbvia (era o elo que faltava). */}
        <div className="flex gap-2 flex-wrap justify-center mb-8">
          {CATEGORIES.map(cat => {
            const pal = p(cat.id);
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200"
                style={
                  activeCategory === cat.id
                    ? { background: pal.accentSoft, border: `1px solid ${pal.accent}99`, color: '#fff' }
                    : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.55)' }
                }
              >
                <span>{cat.emoji}</span>
                <span>{cat.label}</span>
              </button>
            );
          })}
        </div>

        {/* Grid de tiragens */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map(spread => {
            const pal = p(spread.category);
            const isHovered = hovered === spread.id;
            const isFree = spread.id === 'tarot-dia' && !!promo?.active;
            const affordable = canAfford(user?.tokens_balance, 1, isFree);
            return (
              <div
                key={spread.id}
                onMouseEnter={() => setHovered(spread.id)}
                onMouseLeave={() => setHovered(null)}
                className="flex flex-col rounded-2xl overflow-hidden transition-all duration-250"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: `1px solid ${isHovered ? pal.accent + '55' : 'rgba(255,255,255,0.08)'}`,
                  transform: isHovered ? 'translateY(-3px)' : 'none',
                  boxShadow: isHovered ? `0 8px 32px ${pal.glow}` : 'none',
                  opacity: affordable ? 1 : 0.6,
                }}
              >
                <div className="h-1 w-full" style={{ background: pal.bar }} />
                <div className="flex flex-col flex-1 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                      style={{ background: pal.iconBg, boxShadow: isHovered ? `0 0 16px ${pal.glow}` : 'none' }}>
                      {EMOJI[spread.category]}
                    </div>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
                      style={{ background: pal.accentSoft, color: pal.accent }}>
                      {spread.cardCount} carta{spread.cardCount > 1 ? 's' : ''}
                    </span>
                  </div>
                  <h3 className="text-white font-semibold text-base mb-1.5 leading-snug">{spread.name}</h3>
                  <p className="text-white/45 text-sm leading-relaxed flex-1 mb-3">{spread.description}</p>
                  <div className="mb-3">
                    <TokenCostBadge cost={1} free={isFree} balance={user?.tokens_balance} />
                  </div>
                  <button
                    onClick={() => navigate(affordable ? `/consultation?spread=${spread.id}` : '/checkout')}
                    className="w-full py-2.5 rounded-xl text-white text-sm font-semibold transition-opacity duration-200"
                    style={affordable
                      ? { background: pal.btn, opacity: isHovered ? 1 : 0.8 }
                      : { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)' }}
                  >
                    {affordable ? 'Iniciar Consulta' : 'Comprar tokens'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TarotPage;
